import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, FileText, Music, Image, Video, Filter, FolderOpen, Play, Pause } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getCategoryInfo } from './galleryCategories';

// Helper function for file icons - moved outside component for accessibility
const getFileIcon = (type) => {
  switch(type) {
    case 'image': return <Image className="gallery-type-icon" />;
    case 'video': return <Video className="gallery-type-icon" />;
    case 'audio': return <Music className="gallery-type-icon" />;
    case 'psd': return <Image className="gallery-type-icon" />;
    default: return <FileText className="gallery-type-icon" />;
  }
};

// Simplified lazy loading hook - more aggressive loading for better UX
const useLazyLoading = () => {
  const [loadedItems, setLoadedItems] = useState(new Set());
  const observer = useRef();

  const lazyRef = useCallback((node) => {
    if (!node) return;
    
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const itemId = entry.target.dataset.itemId;
            if (itemId) {
              setLoadedItems(prev => new Set([...prev, itemId]));
              observer.current.unobserve(entry.target);
            }
          }
        });
      },
      {
        threshold: 0.01,
        rootMargin: '200px',
      }
    );
    
    if (node) {
      observer.current.observe(node);
      
      // Force immediate loading for items already in viewport
      const itemId = node.dataset.itemId;
      if (itemId) {
        const rect = node.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInViewport) {
          setLoadedItems(prev => new Set([...prev, itemId]));
        }
      }
    }
  }, []);

  // Function to reset loaded items when needed
  const resetLoadedItems = useCallback(() => {
    setLoadedItems(new Set());
  }, []);

  // Function to force load all items (fallback)
  const loadAllItems = useCallback((items) => {
    const allIds = items.map(item => item.id);
    setLoadedItems(new Set(allIds));
  }, []);

  return [loadedItems, lazyRef, resetLoadedItems, loadAllItems];
};

// Optimized Gallery Item Component - reduced memo optimization to ensure proper updates
const GalleryItemMemo = React.memo(({ item, onItemClick, playingVideos, onVideoToggle, loadedItems, lazyRef, t, filter, categoryFilter }) => {
  const isLoaded = loadedItems.has(item.id);
  
  // Get category info dynamically so it updates with language changes
  const categoryInfo = useMemo(() => getCategoryInfo(item.category, t), [item.category, t]);
  
  const handleVideoClick = useCallback((e, videoElement) => {
    e.stopPropagation();
    onVideoToggle(item.id, videoElement);
  }, [item.id, onVideoToggle]);



  return (
    <div 
      key={item.id} 
      className="aped-gallery-item" 
      onClick={() => onItemClick(item)}
      ref={lazyRef}
      data-item-id={item.id}
    >
      <div className="aped-gallery-item-preview">
        {item.type === 'image' && (
          <>
            {!isLoaded ? (
              <div className="aped-gallery-placeholder">
                <Image className="aped-gallery-placeholder-icon" />
              </div>
            ) : (
              <img 
                src={item.path} 
                alt={item.name} 
                className="aped-gallery-image" 
                loading="eager"
              />
            )}
            
            {/* Fallback: Show image after 3 seconds regardless of lazy loading */}
            {!isLoaded && (
              <img 
                src={item.path} 
                alt={item.name} 
                className="aped-gallery-image" 
                style={{
                  opacity: 0,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onLoad={(e) => {
                  setTimeout(() => {
                    e.target.style.opacity = 1;
                    e.target.style.zIndex = 1;
                  }, 3000);
                }}
              />
            )}
          </>
        )}
        {item.type === 'video' && (
          <div className="aped-gallery-video-container">
            {!isLoaded ? (
              <div className="aped-gallery-placeholder">
                <Video className="aped-gallery-placeholder-icon" />
              </div>
            ) : (
              <>
                <video 
                  src={item.path} 
                  className="aped-gallery-video"
                  muted={false}
                  loop={false}
                  preload="metadata"
                  controls={false}
                  onEnded={() => onVideoToggle(item.id, null, true)}
                />
                <div 
                  className="aped-gallery-video-overlay"
                  onClick={(e) => {
                    const videoElement = e.target.previousElementSibling;
                    handleVideoClick(e, videoElement);
                  }}
                >
                  <div className="aped-gallery-play-button">
                    {playingVideos.has(item.id) ? (
                      <Pause className="aped-gallery-play-icon" />
                    ) : (
                      <Play className="aped-gallery-play-icon" />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {(item.type === 'audio' || item.type === 'pdf' || item.type === 'psd' || item.type === 'other') && (
          <div className="aped-gallery-file-preview">
            {getFileIcon(item.type)}
            <span className="aped-gallery-file-name">{item.name}</span>
          </div>
        )}
      </div>
      <div className="aped-gallery-item-overlay">
        <div className="aped-gallery-item-info">
          <div className="aped-gallery-item-type-info">
            {getFileIcon(item.type)}
            <span className="aped-gallery-item-type">{item.type.toUpperCase()}</span>
          </div>
          <div className="aped-gallery-item-category">
            <FolderOpen size={14} />
            <span className="aped-gallery-item-category-title">{categoryInfo.title}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

const Gallery = () => {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [galleryItems, setGalleryItems] = useState([]);
  const [playingVideos, setPlayingVideos] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [loadedItems, lazyRef, resetLoadedItems, loadAllItems] = useLazyLoading();

  // Optimized gallery items loading with better memory management
  useEffect(() => {
    const importGalleryFiles = async () => {
      try {
        setIsLoading(true);
        const galleryContext = require.context('../assets/gallery', true, /\.(jpg|jpeg|png|mp4|mp3|pdf)$/i);
        
        // Process items in smaller batches to avoid blocking the main thread
        const keys = galleryContext.keys();
        const batchSize = 10;
        const items = [];
        
        for (let i = 0; i < keys.length; i += batchSize) {
          const batch = keys.slice(i, i + batchSize);
          const batchItems = batch.map(item => {
            const path = galleryContext(item);
            const fileName = item.replace('./', '');
            const fileNameOnly = fileName.split('/').pop();
            const extension = fileNameOnly.toLowerCase().split('.').pop();
            const category = fileName.includes('/') ? fileName.split('/')[0] : 'uncategorized';
            
            let type = 'other';
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
              type = 'image';
            } else if (['mp4', 'mov', 'avi', 'webm'].includes(extension)) {
              type = 'video';
            } else if (['mp3', 'wav', 'ogg', 'aac'].includes(extension)) {
              type = 'audio';
            } else if (['pdf'].includes(extension)) {
              type = 'pdf';
            }
            
            // Remove unused categoryInfo to fix ESLint warning
            
            return {
              id: fileName,
              name: fileNameOnly,
              fullPath: fileName,
              path: path.default || path,
              type,
              extension,
              category,
              // Don't store translated titles - get them dynamically
              categoryTitle: null,
              categoryDescription: null
            };
          });
          
          items.push(...batchItems);
          
          // Allow other tasks to run
          if (i + batchSize < keys.length) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
        
        setGalleryItems(items);
        
        // Force load all items after a short delay if lazy loading fails
        setTimeout(() => {
          loadAllItems(items);
        }, 2000);
        
      } catch (error) {
        console.error('Error loading gallery items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    importGalleryFiles();
  }, [t, loadAllItems]);

  // Reset lazy loading when filters change to ensure proper re-rendering
  useEffect(() => {
    resetLoadedItems();
  }, [filter, categoryFilter, resetLoadedItems]);

  // Memoized filtered items for better performance
  const filteredItems = useMemo(() => {
    return galleryItems.filter(item => {
      const typeMatch = filter === 'all' || item.type === filter;
      const categoryMatch = categoryFilter === 'all' || item.category === categoryFilter;
      return typeMatch && categoryMatch;
    });
  }, [galleryItems, filter, categoryFilter]);

  // Memoized filter counts to avoid recalculation on every render
  const filterCounts = useMemo(() => ({
    all: galleryItems.length,
    image: galleryItems.filter(item => item.type === 'image').length,
    video: galleryItems.filter(item => item.type === 'video').length,
    audio: galleryItems.filter(item => item.type === 'audio').length,
    pdf: galleryItems.filter(item => item.type === 'pdf').length,
    psd: galleryItems.filter(item => item.type === 'psd').length,
  }), [galleryItems]);

  // Memoized unique categories
  const uniqueCategories = useMemo(() => {
    return [...new Set(galleryItems.map(item => item.category))];
  }, [galleryItems]);

  const categoryCounts = useMemo(() => ({
    all: galleryItems.length,
    ...Object.fromEntries(
      uniqueCategories.map(category => [
        category,
        galleryItems.filter(item => item.category === category).length
      ])
    )
  }), [galleryItems, uniqueCategories]);

  const openModal = useCallback((item) => {
    setSelectedItem(item);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setSelectedItem(null);
    document.body.style.overflow = 'auto';
    // Clean up any playing videos when closing modal
    setPlayingVideos(new Set());
  }, []);

  // Optimized video toggle with memory cleanup
  const toggleVideoPlay = useCallback((videoId, videoElement, isEnded = false) => {
    setPlayingVideos(prevPlayingVideos => {
      const newPlayingVideos = new Set(prevPlayingVideos);
      
      if (isEnded || prevPlayingVideos.has(videoId)) {
        if (videoElement && !isEnded) {
          videoElement.pause();
        }
        newPlayingVideos.delete(videoId);
      } else {
        if (videoElement) {
          // Pause other videos first for better performance
          prevPlayingVideos.forEach(id => {
            if (id !== videoId) {
              const otherVideo = document.querySelector(`[data-item-id="${id}"] video`);
              if (otherVideo) {
                otherVideo.pause();
              }
            }
          });
          newPlayingVideos.clear();
          
          // Unmute the video when playing
          videoElement.muted = false;
          videoElement.play().catch(error => {
            console.warn('Video play failed:', error);
            // Fallback: try playing muted if unmuted fails
            videoElement.muted = true;
            videoElement.play();
          });
          newPlayingVideos.add(videoId);
        }
      }
      
      return newPlayingVideos;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
      // Pause all videos on unmount
      playingVideos.forEach(videoId => {
        const video = document.querySelector(`[data-item-id="${videoId}"] video`);
        if (video) {
          video.pause();
        }
      });
    };
  }, [playingVideos]);

  // Enhanced filter handlers
  const handleFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
    // Clear playing videos when changing filters
    setPlayingVideos(new Set());
  }, []);

  const handleCategoryFilterChange = useCallback((newCategoryFilter) => {
    setCategoryFilter(newCategoryFilter);
    // Clear playing videos when changing filters
    setPlayingVideos(new Set());
  }, []);



  return (
    <>
      <section className="aped-gallery-section">
        <div className="aped-gallery-content-wrapper">
          <h2 className="aped-gallery-title">{t('gallery.title')}</h2>
          <p className="aped-gallery-subtitle">
            {t('gallery.subtitle')}
          </p>

          {/* File Type Filter Buttons */}
          <div className="aped-gallery-filters">
            <h3 className="aped-filter-section-title">{t('gallery.filters.filterByType')}</h3>
            <div className="aped-filter-buttons">
              {[
                { key: 'all', label: t('gallery.filters.all'), icon: <Filter /> },
                { key: 'image', label: t('gallery.filters.images'), icon: <Image /> },
                { key: 'video', label: t('gallery.filters.videos'), icon: <Video /> },
                { key: 'audio', label: t('gallery.filters.audio'), icon: <Music /> },
                { key: 'pdf', label: t('gallery.filters.documents'), icon: <FileText /> },
                { key: 'psd', label: 'PSD', icon: <Image /> }
              ].map(filterType => (
                filterCounts[filterType.key] > 0 && (
                  <button
                    key={filterType.key}
                    className={`aped-gallery-filter-btn ${filter === filterType.key ? 'active' : ''}`}
                    onClick={() => handleFilterChange(filterType.key)}
                  >
                    {filterType.icon}
                    <span>{filterType.label}</span>
                    <span className="aped-filter-count">({filterCounts[filterType.key]})</span>
                  </button>
                )
              ))}
            </div>
          </div>

          {/* Category Filter Buttons */}
          {uniqueCategories.length > 0 && (
            <div className="aped-gallery-filters">
              <h3 className="aped-filter-section-title">{t('gallery.filters.filterByCategory')}</h3>
              <div className="aped-filter-buttons">
                <button
                  className={`aped-gallery-filter-btn ${categoryFilter === 'all' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilterChange('all')}
                >
                  <FolderOpen />
                  <span>{t('gallery.filters.allCategories')}</span>
                  <span className="aped-filter-count">({categoryCounts.all})</span>
                </button>
                {uniqueCategories.map(category => {
                  const categoryInfo = getCategoryInfo(category, t);
                  return (
                    <button
                      key={category}
                      className={`aped-gallery-filter-btn ${categoryFilter === category ? 'active' : ''}`}
                      onClick={() => handleCategoryFilterChange(category)}
                    >
                      <FolderOpen />
                      <span>{categoryInfo.title}</span>
                      <span className="aped-filter-count">({categoryCounts[category]})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gallery Grid */}
          {isLoading ? (
            <div className="aped-gallery-loading">
              <div className="aped-gallery-loading-spinner"></div>
              <p>{t('gallery.loading', 'Loading gallery...')}</p>
            </div>
          ) : (
            <div className="aped-gallery-grid">
              {filteredItems.map((item) => (
                <GalleryItemMemo
                  key={`${item.id}-${filter}-${categoryFilter}`}
                  item={item}
                  onItemClick={openModal}
                  playingVideos={playingVideos}
                  onVideoToggle={toggleVideoPlay}
                  loadedItems={loadedItems}
                  lazyRef={lazyRef}
                  t={t}
                  filter={filter}
                  categoryFilter={categoryFilter}
                />
              ))}
            </div>
          )}

          {filteredItems.length === 0 && !isLoading && (
            <div className="aped-gallery-empty">
              <p>{t('gallery.empty')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      {selectedItem && (
        <div className="aped-gallery-modal" onClick={closeModal}>
          <div className="aped-gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="aped-gallery-modal-header">
              <div className="aped-gallery-modal-title-section">
                <h3 className="aped-gallery-modal-title">{selectedItem.name}</h3>
                <div className="aped-gallery-modal-category">
                  <FolderOpen size={16} />
                  <span>{getCategoryInfo(selectedItem.category, t).title}</span>
                </div>
              </div>
              <div className="aped-gallery-modal-actions">
                <button
                  className="aped-gallery-modal-btn aped-gallery-modal-close"
                  onClick={closeModal}
                  title={t('gallery.close')}
                >
                  <X />
                </button>
              </div>
            </div>
            
            <div className="aped-gallery-modal-body">
              {selectedItem.type === 'image' && (
                <img src={selectedItem.path} alt={selectedItem.name} className="aped-gallery-modal-image" />
              )}
              {selectedItem.type === 'video' && (
                <video 
                  src={selectedItem.path} 
                  controls 
                  autoPlay={false}
                  muted={false}
                  className="aped-gallery-modal-video"
                  preload="metadata"
                  onEnded={() => toggleVideoPlay(`modal-${selectedItem.id}`, null, true)}
                />
              )}
              {selectedItem.type === 'pdf' && (
                <iframe src={selectedItem.path} className="aped-gallery-modal-pdf-viewer" title={selectedItem.name} />
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .aped-gallery-section {
          padding: 6rem 2rem 4rem;
          background: linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%);
          font-family: 'Inter', sans-serif;
          contain: layout style paint;
        }
        
        .aped-gallery-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }
        
        .aped-gallery-loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #b4a35e;
          border-radius: 50%;
          animation: aped-spin 1s linear infinite;
          margin-bottom: 1rem;
        }
        
        @keyframes aped-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .aped-gallery-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        
        .aped-gallery-placeholder-icon {
          width: 48px;
          height: 48px;
          color: #999;
          opacity: 0.7;
        }

        .aped-gallery-content-wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }

        .aped-gallery-title {
          font-size: 2.5rem;
          color: #1b1f3a;
          margin-bottom: 1rem;
          text-align: center;
          position: relative;
        }

        .aped-gallery-title::after {
          content: '';
          display: block;
          width: 50px;
          height: 3px;
          background-color: #b4a35e;
          margin: 1rem auto 0;
        }

        .aped-gallery-subtitle {
          text-align: center;
          color: #666;
          font-size: 1.1rem;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .aped-gallery-filters {
          margin-bottom: 2rem;
        }

        .aped-filter-section-title {
          text-align: center;
          color: #1b1f3a;
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 1rem;
          margin-top: 0;
        }

        .aped-filter-buttons {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .aped-gallery-filter-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 25px;
          color: #666;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .aped-gallery-filter-btn:hover {
          border-color: #b4a35e;
          color: #b4a35e;
          transform: translateY(-2px);
        }

        .aped-gallery-filter-btn.active {
          background: #b4a35e;
          border-color: #b4a35e;
          color: white;
        }

        .aped-filter-count {
          background: rgba(0, 0, 0, 0.1);
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.8rem;
        }

        .aped-gallery-filter-btn.active .aped-filter-count {
          background: rgba(255, 255, 255, 0.2);
        }

        .aped-gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 2rem;
        }

        .aped-gallery-item {
          position: relative;
          border-radius: 15px;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: white;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          will-change: transform;
          contain: layout style paint;
        }

        .aped-gallery-item:hover {
          transform: translateY(-5px) translateZ(0);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }

        .aped-gallery-item-preview {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
        }

        .aped-gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
          backface-visibility: hidden;
        }

        .aped-gallery-item:hover .aped-gallery-image {
          transform: scale(1.05) translateZ(0);
        }

        .aped-gallery-video-preview {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .aped-gallery-video-container {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .aped-gallery-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          will-change: transform;
          backface-visibility: hidden;
        }

        .aped-gallery-video-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .aped-gallery-video-overlay:hover {
          background: rgba(0, 0, 0, 0.3);
        }

        .aped-gallery-play-button {
          background: rgba(0, 0, 0, 0.7);
          border-radius: 50%;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          transform: scale(0.8);
        }

        .aped-gallery-video-overlay:hover .aped-gallery-play-button {
          background: rgba(180, 163, 94, 0.9);
          transform: scale(1);
        }

        .aped-gallery-play-icon {
          color: white;
          width: 24px;
          height: 24px;
          margin-left: 2px;
        }



        .aped-gallery-file-preview {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(135deg, #b4a35e 0%, #c9b87d 100%);
          color: white;
          text-align: center;
          padding: 2rem;
        }

        .gallery-type-icon {
          width: 48px;
          height: 48px;
          margin-bottom: 1rem;
        }

        .aped-gallery-file-name {
          font-size: 0.9rem;
          font-weight: 500;
          word-break: break-word;
        }

        .aped-gallery-item-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
          padding: 1rem;
          transform: translateY(100%);
          transition: transform 0.3s ease;
        }

        .aped-gallery-item:hover .aped-gallery-item-overlay {
          transform: translateY(0);
        }

        .aped-gallery-item-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          color: white;
        }

        .aped-gallery-item-type-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .aped-gallery-item-type {
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .aped-gallery-item-category {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          opacity: 0.9;
        }

        .aped-gallery-item-category-title {
          font-size: 0.75rem;
          font-weight: 500;
        }

        .aped-gallery-empty {
          text-align: center;
          padding: 4rem 2rem;
          color: #666;
          font-size: 1.1rem;
        }

        /* Modal Styles */
        .aped-gallery-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .aped-gallery-modal-content {
          background: white;
          border-radius: 15px;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .aped-gallery-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.5rem;
          border-bottom: 1px solid #e0e0e0;
        }

        .aped-gallery-modal-title-section {
          flex: 1;
        }

        .aped-gallery-modal-title {
          font-size: 1.25rem;
          color: #1b1f3a;
          margin: 0 0 0.5rem 0;
          word-break: break-word;
        }

        .aped-gallery-modal-category {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #666;
          font-size: 0.9rem;
        }

        .aped-gallery-modal-actions {
          display: flex;
          gap: 0.5rem;
        }

        .aped-gallery-modal-btn {
          background: #f0f0f0;
          border: none;
          border-radius: 8px;
          padding: 0.5rem;
          cursor: pointer;
          transition: background 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .aped-gallery-modal-btn:hover {
          background: #e0e0e0;
        }

        .aped-gallery-modal-close:hover {
          background: #ff6b6b;
          color: white;
        }

        .aped-gallery-modal-body {
          padding: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
        }

        .aped-gallery-modal-image {
          max-width: 100%;
          max-height: 70vh;
          object-fit: contain;
          border-radius: 8px;
        }

        .aped-gallery-modal-video {
          max-width: 100%;
          max-height: 70vh;
          border-radius: 8px;
        }

        .aped-gallery-modal-audio {
          text-align: center;
        }

        .aped-gallery-modal-audio-icon {
          width: 64px;
          height: 64px;
          color: #b4a35e;
          margin-bottom: 2rem;
        }

        .aped-gallery-modal-audio-player {
          width: 100%;
          max-width: 400px;
        }

        .aped-gallery-modal-pdf-viewer {
          width: 100%;
          height: 70vh;
          border: none;
        }

        /* Mobile Responsive - Optimized for low-end devices */
        @media (max-width: 768px) {
          .aped-gallery-section {
            padding: 4rem 1rem 2rem;
            /* Reduce animations on mobile for better performance */
            transform: translateZ(0);
          }

          .aped-gallery-title {
            font-size: 2rem;
          }

          .aped-gallery-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
            gap: 1rem;
            /* Optimize grid for mobile performance */
            contain: layout style;
          }

          .aped-gallery-item {
            /* Simplify animations on mobile */
            transition: transform 0.2s ease;
          }
          
          .aped-gallery-item:hover {
            transform: translateY(-2px) translateZ(0);
          }

          .aped-gallery-item-preview {
            height: 120px;
          }

          .aped-gallery-filters {
            gap: 0.5rem;
          }

          .aped-gallery-filter-btn {
            padding: 0.5rem 1rem;
            font-size: 0.9rem;
          }

          .aped-gallery-modal {
            padding: 1rem;
          }

          .aped-gallery-modal-header {
            padding: 1rem;
          }

          .aped-gallery-modal-body {
            padding: 1rem;
            min-height: 300px;
          }
          
          /* Optimize placeholders for mobile */
          .aped-gallery-placeholder-icon {
            width: 32px;
            height: 32px;
          }
        }

        /* Very small devices optimization */
        @media (max-width: 480px) {
          .aped-gallery-grid {
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 0.75rem;
          }
          
          .aped-gallery-item-preview {
            height: 100px;
          }
          
          .aped-filter-buttons {
            gap: 0.5rem;
          }
          
          .aped-gallery-filter-btn {
            padding: 0.4rem 0.8rem;
            font-size: 0.8rem;
          }
          
          /* Disable hover effects on very small devices for performance */
          .aped-gallery-item:hover {
            transform: none;
          }
          
          .aped-gallery-item:hover .aped-gallery-image {
            transform: none;
          }
        }

        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          .aped-gallery-item,
          .aped-gallery-image,
          .aped-gallery-filter-btn,
          .aped-gallery-loading-spinner {
            transition: none;
            animation: none;
          }
          
          .aped-gallery-item:hover {
            transform: none;
          }
          
          .aped-gallery-item:hover .aped-gallery-image {
            transform: none;
          }
        }
      `}</style>
    </>
  );
};

export default Gallery;